import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSaveForm } from '@/shared/lib/useSaveForm';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { FormField } from '@/shared/ui/FormField';
import { ModalDialog } from '@/shared/ui/ModalDialog';
import { createWork, editWork, transliterateWorkName, type SeoWorkType } from './api';

const ACTIVE_OPTIONS = [
  { value: '0', label: 'нет' },
  { value: '1', label: 'да' },
];

type Props = {
  /** Edit this work type; when omitted a new one is created under `parentId`. */
  work?: SeoWorkType;
  /** 0 creates a section. */
  parentId?: number;
  onClose: () => void;
};

interface FormValues {
  name: string;
  slug: string;
  isActive: number;
}

export const WorkModal = ({ work, parentId = 0, onClose }: Props) => {
  const qc = useQueryClient();
  const [name, setName] = useState(work?.name ?? '');
  const [slug, setSlug] = useState(work?.slug ?? '');
  const [isActive, setIsActive] = useState(String(work?.is_active ?? 0));

  const saveForm = useSaveForm(
    (values: FormValues) =>
      work ? editWork({ id: work.id, ...values }) : createWork({ ...values, parentId }),
    () => {
      void qc.invalidateQueries({ queryKey: ['seo-work'] });
      onClose();
    },
  );
  const generate = useSaveForm(transliterateWorkName, setSlug);

  const errors = { ...generate.errors, ...saveForm.errors };
  const clearAll = (field: string) => {
    saveForm.clearErrors(field);
    generate.clearErrors(field);
  };

  return (
    <ModalDialog
      onClose={onClose}
      isSaving={saveForm.isPending}
      onSave={() => saveForm.save({ name, slug, isActive: Number(isActive) })}
    >
      <FormField label="Название" errors={errors.name ?? errors.work_type_name}>
        <input
          className="form-control input-sm"
          style={{ width: 250 }}
          placeholder="Название..."
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            clearAll('name');
          }}
        />
      </FormField>
      <FormField label="Активировано для автосервисов" errors={errors.is_active}>
        <FilterSelect
          options={ACTIVE_OPTIONS}
          value={isActive}
          isClearable={false}
          onChange={(value) => {
            setIsActive(value);
            clearAll('is_active');
          }}
        />
      </FormField>
      <FormField label="Slug" errors={errors.slug}>
        <input
          className="form-control input-sm"
          style={{ width: 250 }}
          placeholder="Слаг..."
          value={slug}
          onChange={(e) => {
            setSlug(e.target.value);
            clearAll('slug');
          }}
        />
        {!work && (
          <button
            type="button"
            className="btn btn-success btn-sm"
            style={{ marginTop: 6 }}
            disabled={generate.isPending}
            onClick={() => generate.save(name)}
          >
            Сгенерировать
          </button>
        )}
      </FormField>
      {errors.work_type_id && <p className="text-red">{errors.work_type_id[0]}</p>}
      {errors.common && <p className="text-red">{errors.common[0]}</p>}
    </ModalDialog>
  );
};
